// Core packages
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

// Third party packages
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, Observable, of, Subscription, throwError } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";

// Custom packages
import { findRequestedUserRole } from "src/app/modules/auth/requested-user-role.utils";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { IUser, UserRole } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { EnvironmentService } from "src/app/shared/services/environment.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { TankService } from "../tank/tank.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private subscriptions: Subscription[] = [];
  loggedUser$ = new BehaviorSubject<IUser>(null);
  loggedUserRequestedRole$ = new BehaviorSubject<string>("");
  loggedUserRequestedRoleType$ = new BehaviorSubject<string>("");
  loggedUserRequestedRoleObj$ = new BehaviorSubject<any>(null);
  loggedUserPermissions$ = new BehaviorSubject<string[]>([]);
  sessionExpiresAt$ = new BehaviorSubject<number>(0);
  isRoleChanging$ = new BehaviorSubject<boolean>(false);
  tankDocumentsCount$ = new BehaviorSubject<number>(0);
  historicalDocsCount$ = new BehaviorSubject<number>(0);

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private router: Router,
    private cookieService: CookieService,
    private environmentService: EnvironmentService,
    private configService: ConfigService,
    private helperService: HelperService,
    private tankService: TankService,
    private documentArchivesService: DocumentArchivesService
  ) {}

  /**
   * Log user in through apiService
   *
   * @since 1.0.0
   *
   * @param email User email
   * @param password User password
   */
  login(email: string, password: string): Observable<any> {
    return this.apiService
      .apiCall(
        "POST",
        "auth/login",
        { email, password },
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        map((result: any) => {
          if (result.status) {
            try {
              this.setSession(result);
              const expiresAt = moment().add(result.expiresIn, "second").utc();
              this.loggedUser$.next(result.user);
              this.loggedUserRequestedRole$.next(result.user.userRoles[0]._id); // this.loggedUserRequestedRole$.next(result.user.roles[0]._id);
              this.loggedUserRequestedRoleType$.next(
                result.user.userRoles[0].role.type
              ); // this.loggedUserRequestedRole$.next(result.user.roles[0]._id);
              this.loggedUserRequestedRoleObj$.next(result.user.userRoles[0]);
              // Get tankDocumentsCount
              this.subscriptions.push(
                this.tankService.getList().subscribe(
                  (tankRes: any) => {
                    if (tankRes && tankRes.status) {
                      const tankData = tankRes.data || tankRes;
                      if (tankData && tankData.totalCount) {
                        this.tankDocumentsCount$.next(tankData.totalCount);
                      }
                    }
                  },
                  (tankErr) => {
                    this.helperService.handleError(tankErr);
                  }
                )
              );
              const userPermissions = [];
              for (const permission of result.user.userRoles[0].role
                .permissions) {
                // for (const permission of result.user.roles[0].permissions) {
                if (
                  this.manageGeneralPermission(
                    result.user.userRoles[0].role.type,
                    result.user.userRoles[0].generalPermission,
                    permission.permissionId.key
                  )
                ) {
                  userPermissions.push(permission.permissionId.key);
                }
              }
              this.checkLastUserDocs(userPermissions);
              this.loggedUserPermissions$.next(userPermissions);
              this.sessionExpiresAt$.next(expiresAt.valueOf());
              return result;
            } catch (error) {
              // Something went wrong with session setting, let's logout user
              console.error("Error during session setting:\n", error);
              this.logout();
              return {
                status: false,
              };
            }
          }
          console.error("Error during session setting");
          return {
            status: false,
          };
        })
      );
  }

  /**
   * Send an email with a reset-password token to the user
   *
   * @since 1.0.0
   *
   * @param email User email
   */
  recoverPassword(email: string): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      "auth/recover-password",
      { email },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Check if current user is logged in
   *
   * @since 1.0.0
   *
   * @returns Observable<any>
   */
  checkLogin(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "auth/check",
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        tap((result: any) => {
          if (result.status) {
            const user = result.data;
            if (!user || !user.userRoles || !user.userRoles.length) {
              console.error('checkLogin: invalid user data received');
              return;
            }
            const expiresAt = moment().add(result.expiresIn, "second");
            this.loggedUser$.next(user);
            if (this.cookieService.get("requestedRole")) {
              // The entry of the requested role IN the requested company, as the back-end picks
              // it: the same role held in several companies matches the role id more than once
              const reqRole = findRequestedUserRole<UserRole>(
                user.userRoles,
                this.cookieService.get("requestedRole"),
                this.cookieService.get("requestedCompany")
              ); // const reqRole = user.userRoles.find(role => role._id === this.cookieService.get('requestedRole'));
              if (!reqRole) {
                console.error("Cannot find reqRole!");
                return;
              }

              this.loggedUserRequestedRole$.next(reqRole._id);
              this.loggedUserRequestedRoleType$.next(reqRole.role.type);
              this.loggedUserRequestedRoleObj$.next(reqRole);
              const userPermissions = [];
              for (const permission of reqRole.role.permissions) {
                // for (const permission of reqRole.permissions) {
                if (
                  this.manageGeneralPermission(
                    reqRole.role.type,
                    reqRole.generalPermission,
                    permission.permissionId.key
                  )
                ) {
                  userPermissions.push(permission.permissionId.key);
                }
              }
              this.checkLastUserDocs(userPermissions);
              this.loggedUserPermissions$.next(userPermissions);
            } else {
              this.loggedUserRequestedRole$.next(user.userRoles[0]._id);
              this.loggedUserRequestedRoleType$.next(
                user.userRoles[0].role.type
              );
              this.loggedUserRequestedRoleObj$.next(user.userRoles[0]);

              const userPermissions = [];
              for (const permission of user.userRoles[0].role.permissions) {
                if (
                  this.manageGeneralPermission(
                    user.userRoles[0].role.type,
                    user.userRoles[0].generalPermission,
                    permission.permissionId.key
                  )
                ) {
                  userPermissions.push(permission.permissionId.key);
                }
              }
              this.checkLastUserDocs(userPermissions);
              this.loggedUserPermissions$.next(userPermissions);
            }
            this.sessionExpiresAt$.next(expiresAt.valueOf());
            // Get tankDocumentsCount
            this.subscriptions.push(
              this.tankService.getList().subscribe(
                (tankRes: any) => {
                  if (tankRes && tankRes.status) {
                    const tankData = tankRes.data || tankRes;
                    if (tankData && tankData.totalCount) {
                      this.tankDocumentsCount$.next(tankData.totalCount);
                    }
                  }
                },
                (tankErr) => {
                  this.helperService.handleError(tankErr);
                }
              )
            );
          }
        })
      );
  }

  /**
   * Log user out
   *
   * @since 1.0.0
   *
   * @param redirect True if after logout there should be a redirect
   * to login page. Default false.
   * @returns boolean true if user was logged out, false otherwhise
   */
  logout(redirect?: boolean): boolean {
    const withNavigate = redirect ? redirect : false;
    // this.cookieService.delete('s.id', '/', this.environmentService.getFrontEndHost());
    // this.cookieService.delete('s.expiresAt', '/', this.environmentService.getFrontEndHost());
    // this.cookieService.delete('s.refreshToken', '/', this.environmentService.getFrontEndHost());
    // this.cookieService.delete('s.refreshTokenExpiresAt', '/', this.environmentService.getFrontEndHost());
    // this.cookieService.delete('requestedRole', '/', this.environmentService.getFrontEndHost());
    // this.cookieService.delete('requestedCompany', '/', this.environmentService.getFrontEndHost());
    // Delete cookies
    this.cookieService.set(
      "s.id",
      "",
      new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    this.cookieService.set(
      "s.expiresAt",
      "",
      new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    this.cookieService.set(
      "s.refreshToken",
      "",
      new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    this.cookieService.set(
      "s.refreshTokenExpiresAt",
      "",
      new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    this.cookieService.set(
      "requestedRole",
      "",
      new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    this.cookieService.set(
      "requestedCompany",
      "",
      new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );

    this.loggedUser$.next(null);
    this.loggedUserRequestedRole$.next("");
    this.loggedUserRequestedRoleType$.next("");
    this.loggedUserRequestedRoleObj$.next({});
    this.loggedUserPermissions$.next([]);
    this.sessionExpiresAt$.next(0);
    this.tankDocumentsCount$.next(0);
    this.historicalDocsCount$.next(0);
    if (withNavigate) {
      const lang =
        this.cookieService.get("lang") || this.configService.defaultLang;
      this.router.navigate(["/", lang, "auth", "login"]);
    }

    return true;
  }

  /**
   * Refresh current token (s.id) using refresh token
   *
   * @since 1.0.0
   *
   * @returns Observable<any>
   */
  refreshToken(redirectIfLogout = true): Observable<boolean> {
    const refreshTokenExp = +this.cookieService.get("s.refreshTokenExpiresAt");
    const refreshToken = this.cookieService.get("s.refreshToken");

    if (
      typeof this.cookieService.get("s.refreshTokenExpiresAt") === undefined ||
      (typeof this.cookieService.get("refreshToken") === undefined &&
        moment.unix(moment.now()) > moment.unix(refreshTokenExp))
    ) {
      this.logout(redirectIfLogout);
      return of(false);
    }

    if (!this.loggedUser$.value || !this.loggedUser$.value._id) {
      this.logout(redirectIfLogout);
      return;
    }

    // The raw token: ApiService adds the "Bearer " prefix itself, and the back-end refused the doubled
    // one (it read "Bearer" as the token)
    return this.apiService
      .apiCall(
        "POST",
        "auth/refreshtoken",
        {},
        { "X-Refresh-Token": refreshToken },
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        switchMap((result: any) => {
          // ApiService emits a network failure (status 0) as an Error value instead of throwing it:
          // the session may still be valid, so the renewal fails without logging out
          if (result instanceof Error) {
            return throwError({ status: 0, error: null });
          }
          this.setSession(result.data);
          const expiresAt = moment().add(result.data.expiresIn, "second");
          this.sessionExpiresAt$.next(expiresAt.valueOf());
          return of(true);
        }),
        catchError((err) => {
          if (err && err.status === 0) {
            return throwError(err);
          }
          this.logout(redirectIfLogout);
          return of(false);
        })
      );
  }

  /**
   * Set given role as the default requested role
   *
   * @since 1.0.0
   */
  setRequestRoleId(id: string, roleId: string): void {
    // Set role id on request header
    this.cookieService.set(
      "requestedRole",
      roleId,
      null,
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    // Set role id on BehaviorSubject
    this.loggedUserRequestedRole$.next(id);
    // Set permissions
    const loggedUserPermissions = [];
    if (this.loggedUser$.getValue()) {
      const requestedRole = this.loggedUser$
        .getValue()
        .userRoles.find((obj) => {
          return obj._id === id;
        });
      if (requestedRole) {
        // Set company reference in request header if company is present...
        if (requestedRole.company) {
          this.cookieService.set(
            "requestedCompany",
            requestedRole.company._id.toString(),
            null,
            "/",
            this.environmentService.getFrontEndHost(),
            this.environmentService.getFrontEndCookieSecure(),
            "Strict"
          );
        } else {
          // ...otherwise remove it from req header
          if (this.cookieService.get("requestedCompany")) {
            // this.cookieService.delete(
            //   'requestedCompany',
            //   '/',
            //   this.environmentService.getFrontEndHost(),
            // );
            // Delete cookie
            this.cookieService.set(
              "requestedCompany",
              "",
              new Date("Thu, 01 Jan 1970 00:00:01 GMT"),
              "/",
              this.environmentService.getFrontEndHost(),
              this.environmentService.getFrontEndCookieSecure(),
              "Strict"
            );
          }
        }

        // Set role type
        if (requestedRole.role && requestedRole.role.type) {
          this.loggedUserRequestedRoleType$.next(requestedRole.role.type);
          this.loggedUserRequestedRoleObj$.next(requestedRole);
        }
        // Set user's permissions
        for (const permission of requestedRole.role.permissions) {
          if (permission.permissionId && permission.permissionId.key) {
            if (
              this.manageGeneralPermission(
                requestedRole.role.type,
                requestedRole.generalPermission,
                permission.permissionId.key
              )
            ) {
              loggedUserPermissions.push(permission.permissionId.key);
            }
          }
        }
        this.checkLastUserDocs(loggedUserPermissions);
        this.loggedUserPermissions$.next(loggedUserPermissions);
      }
    } else {
      // If we came here means loggedUser$ has not been emitted (yet!)
      // Se we must subscribe to that behaviourSubject and wait...
      this.loggedUser$.pipe(take(1)).subscribe((res) => {
        if (res) {
          const requestedRole = res.userRoles.find((obj) => obj._id === id); // const requestedRole = res.roles.find(role => role._id === id);
          if (!requestedRole) {
            return;
          }
          if (requestedRole.company) {
            // Get companyId and set it as req header
            this.cookieService.set(
              "requestedCompany",
              requestedRole.company._id.toString(),
              null,
              "/",
              this.environmentService.getFrontEndHost(),
              this.environmentService.getFrontEndCookieSecure(),
              "Strict"
            );
          }
          // Set user's permissions
          for (const permission of requestedRole.role.permissions) {
            if (permission.permissionId && permission.permissionId.key) {
              if (
                this.manageGeneralPermission(
                  requestedRole.role.type,
                  requestedRole.generalPermission,
                  permission.permissionId.key
                )
              ) {
                loggedUserPermissions.push(permission.permissionId.key);
              }
            }
          }
          this.checkLastUserDocs(loggedUserPermissions);
          this.loggedUserPermissions$.next(loggedUserPermissions);
        }
      });
    }
  }

  /**
   * Emit new data for current logged user
   * This is often used once user update his profile to other
   * components can know changes in real time
   *
   * @since 1.0.0
   */
  updatedLoggedUserData(): void {
    this.checkLogin().subscribe();
  }

  /**
   * Set given login result data as current session
   *
   * @since 1.0.0
   *
   * @param data Session data coming from back-end
   */
  private setSession(data: any): void {
    const expiresAt = moment().add(data.expiresIn, "second");
    this.cookieService.set(
      "s.id",
      data.token,
      null,
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    this.cookieService.set(
      "s.expiresAt",
      JSON.stringify(expiresAt.valueOf()),
      null,
      "/",
      this.environmentService.getFrontEndHost(),
      this.environmentService.getFrontEndCookieSecure(),
      "Strict"
    );
    // Only the login answer carries the user: the renewal of the token (`{ token, expiresIn }`) must
    // not reset the role the user is acting with to the first one
    if (data.user && data.user.userRoles && data.user.userRoles[0]) {
      this.cookieService.set(
        "requestedRole",
        data.user.userRoles[0].role._id,
        // data.user.roles[0]._id,
        null,
        "/",
        this.environmentService.getFrontEndHost(),
        this.environmentService.getFrontEndCookieSecure(),
        "Strict"
      );
      if (data.user.userRoles[0].company) {
        this.cookieService.set(
          "requestedCompany",
          data.user.userRoles[0].company._id.toString(),
          null,
          "/",
          this.environmentService.getFrontEndHost(),
          this.environmentService.getFrontEndCookieSecure(),
          "Strict"
        );
      }
    }
    if (data.refreshToken) {
      this.cookieService.set(
        "s.refreshToken",
        data.refreshToken,
        null,
        "/",
        this.environmentService.getFrontEndHost(),
        this.environmentService.getFrontEndCookieSecure(),
        "Strict"
      );
    }
    if (data.refreshTokenExpiresIn) {
      const refreshExpiresAt = moment().add(data.refreshExpiresIn, "second");
      this.cookieService.set(
        "s.refreshTokenExpiresAt",
        JSON.stringify(refreshExpiresAt.valueOf()),
        null,
        "/",
        this.environmentService.getFrontEndHost(),
        this.environmentService.getFrontEndCookieSecure(),
        "Strict"
      );
    }
  }

  /**
   * Manage General Permission of Admin, Operator, Master and Collaborator user
   *
   * @since 1.0.0
   */
  manageGeneralPermission(roleType, generalPermission, permissionKey): boolean {
    // `null` is read as every switch off, as the back-end reads it (`hasEffectivePerms`): read as
    // it is, the first `generalPermission.<switch>` below would throw and break the session
    if (generalPermission === null) {
      return this.manageGeneralPermission(roleType, {}, permissionKey);
    }
    if (typeof generalPermission === "undefined" && roleType !== "temporary") {
      return true;
    }

    // Amministrazione
    // Accedere e modificare i dati amministrativi e fiscali (date abbonamento, fatturazioni, ecc.)
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.administration === "undefined" ||
      !generalPermission.administration
    ) {
      if (
        permissionKey === "canReadPricesList" ||
        permissionKey === "canCreatePrices" ||
        permissionKey === "canDeletePrices" ||
        permissionKey === "canReadBalancesList" ||
        permissionKey === "canCreateBalances" ||
        permissionKey === "canExportBalances" ||
        permissionKey === "canReadSubscriptionsList" ||
        permissionKey === "canExportSubscriptions" ||
        permissionKey === "canDeleteSubscriptions" ||
        permissionKey === "canCreateSubscriptions"
      ) {
        return false;
      }
    }

    // Azienda
    // Accesso a società per visualizzazione e modifica dati società per Admin e Operator
    // Accesso a società per visualizzazione per Master e Collaborator
    if (
      (roleType === "admin" || roleType === "operator") &&
      (typeof generalPermission === "undefined" ||
        typeof generalPermission.editCompanies === "undefined" ||
        !generalPermission.editCompanies)
    ) {
      if (
        permissionKey === "canDeleteCompanies" ||
        permissionKey === "canCreateCompanies" ||
        permissionKey === "canReadCompaniesList" ||
        permissionKey === "canExportCompanies" ||
        permissionKey === "canViewCompany" ||
        permissionKey === "canUpdateCompanies"
      ) {
        return false;
      }
    }
    if (
      (roleType === "master" || roleType === "collaborator") &&
      (typeof generalPermission === "undefined" ||
        typeof generalPermission.companies === "undefined" ||
        !generalPermission.companies)
    ) {
      if (
        permissionKey === "canDeleteCompanies" ||
        permissionKey === "canCreateCompanies" ||
        permissionKey === "canReadCompaniesList" ||
        permissionKey === "canExportCompanies" ||
        permissionKey === "canViewCompany" ||
        permissionKey === "canUpdateCompanies"
      ) {
        return false;
      }
    }

    // Immobili
    // Accesso immobili per visualizzazione mappatura, rimappatura,
    // visualizzazione database documenti ed esportazione in excel dell’elenco dei documenti inseriti e mancanti
    if (roleType !== "temporary") {
      if (
        typeof generalPermission === "undefined" ||
        typeof generalPermission.properties === "undefined" ||
        !generalPermission.properties
      ) {
        if (
          permissionKey === "canExportProperties" ||
          permissionKey === "canDeleteProperties" ||
          permissionKey === "canCreateProperties" ||
          permissionKey === "canReadPropertiesList" ||
          permissionKey === "canEditProperties" ||
          permissionKey === "canCreateCondoStructures" ||
          permissionKey === "canReadCondoStructuresList" ||
          permissionKey === "canCreateDocuments" ||
          permissionKey === "canReadDocumentsList" ||
          permissionKey === "canDeleteDocuments" ||
          permissionKey === "canSearchDocuments" ||
          permissionKey === "canCreatePaperDocuments" ||
          permissionKey === "canCreateDigitalDocuments" ||
          permissionKey === "canDeleteAgreements" ||
          permissionKey === "canEditDocumentsData"
        ) {
          return false;
        }
      }
    }
    // Immobili
    // Elenco delle prossime scadenze degli immobili
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.timetable === "undefined" ||
      !generalPermission.timetable
    ) {
      if (
        permissionKey === "canExportPropertiesExpirations" ||
        permissionKey === "canReadPropertiesExpirationsList"
      ) {
        return false;
      }
    }
    // Immobili
    // Ricerca documenti
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.docSearch === "undefined" ||
      !generalPermission.docSearch
    ) {
      if (permissionKey === "canSearchDocuments") {
        return false;
      }
    }
    // Immobili
    // Inserimento documenti cartacei
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.insertPaperDoc === "undefined" ||
      !generalPermission.insertPaperDoc
    ) {
      if (permissionKey === "canCreatePaperDocuments") {
        return false;
      }
    }
    // Immobili
    // Inserimento documenti digitali
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.insertDigitalDoc === "undefined" ||
      !generalPermission.insertDigitalDoc
    ) {
      if (permissionKey === "canCreateDigitalDocuments") {
        return false;
      }
    }
    // Immobili
    // Possibilità di eliminare un documento inserito (cestino)
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.delDoc === "undefined" ||
      !generalPermission.delDoc
    ) {
      if (permissionKey === "canDeleteDocuments") {
        return false;
      }
    }
    // Immobili
    // Possibilità di eliminare un contratto inserito (cestino)
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.delAgreement === "undefined" ||
      !generalPermission.delAgreement
    ) {
      if (permissionKey === "canDeleteAgreements") {
        return false;
      }
    }
    // Immobili
    // Possibilità di modificare i campi aggiuntivi dei documenti, tranne quelli con scadenze e i contratti a rinnovo automatico (pennina)
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.editDocData === "undefined" ||
      !generalPermission.editDocData
    ) {
      if (permissionKey === "canEditDocumentsData") {
        return false;
      }
    }

    // Utenti
    // Creare, eliminare e modificare i permessi di accesso dei Collaboratori
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.users === "undefined" ||
      !generalPermission.users
    ) {
      if (
        permissionKey === "canDeleteUsers" ||
        permissionKey === "canReadUsersList" ||
        permissionKey === "canCreateUsers" ||
        permissionKey === "canExportUsers" ||
        permissionKey === "canChangeUsersStatus" ||
        permissionKey === "canCreateCondoUsers" ||
        permissionKey === "canCreateCondoAdmin"
      ) {
        return false;
      }
    }

    // Utenti temporanei
    // Creare, modificare ed eliminare gli accessi temporanei
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.temporaries === "undefined" ||
      !generalPermission.temporaries
    ) {
      if (
        permissionKey === "canDeleteTemporaryUsers" ||
        permissionKey === "canReadTemporaryUsersList" ||
        permissionKey === "canCreateTemporaryUsers" ||
        permissionKey === "canExportTemporaryUsers" ||
        permissionKey === "canChangeTemporaryUsersStatus"
      ) {
        return false;
      }
    }

    // Invio email da piattaforma
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.email === "undefined" ||
      !generalPermission.email
    ) {
      if (
        permissionKey === "canReadCommunicationsList" ||
        permissionKey === "canCreateCommunications" ||
        permissionKey === "canDeleteCommunications"
      ) {
        return false;
      }
    }

    // View Alert of the latest documents inserted
    if (
      typeof generalPermission === "undefined" ||
      typeof generalPermission.alertLastDocs === "undefined" ||
      !generalPermission.alertLastDocs
    ) {
      if (permissionKey === "canViewLatestDocsInsList") {
        return false;
      }
    }
    return true;
  }

  /**
   * Verify that there are the latest documents inserted by the previous check
   *
   * @since 1.0.0
   */
  checkLastUserDocs(loggedUserPermissions: string[]): void {
    if (loggedUserPermissions.indexOf("canViewLatestDocsInsList") >= 0) {
      this.subscriptions.push(
        this.documentArchivesService.checkHistoricalDocs().subscribe(
          (res) => {
            if (res.status) {
              this.historicalDocsCount$.next(res.data.totalCount);
            }
          },
          () => {
            // Only an alert: a refused or failed count shows none, with no error in the console
            this.historicalDocsCount$.next(0);
          }
        )
      );
    } else {
      this.historicalDocsCount$.next(0);
    }
  }
}
